import { type ReactNode, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import {
  getListBrandsV1BrandsGetQueryKey,
  useBulkDeleteV1BrandsBulkDeletePost,
  useCreateBrandV1BrandsPost,
  useListBrandsV1BrandsGet,
  useLowConfidenceCountsV1BrandsMatchesLowConfidenceGet,
  useSetEnabledV1BrandsSetEnabledPost,
  useSetPriorityV1BrandsSetPriorityPost,
  useUpdateBrandV1BrandsBrandIdPatch,
} from "../api/generated/qcsControlPlane";
import type { BrandOut } from "../api/generated/model";
import { countFor as countOf, fetchSkuCounts } from "../api/skuCounts";
import { platformKeys } from "../api/platforms";
import { type Platform, ago, platformLabel } from "../api/labels";
import { useCanWrite } from "../auth/me";
import { AnimatedNumber } from "../lib/motion";
import { SelectAllCheckbox, SelectionCount, useRowSelection } from "../lib/selection";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { BulkImport } from "../components/BulkImport";
import { SkuDownloadModal } from "../components/SkuDownloadModal";
import { PriorityStepper } from "../components/PriorityStepper";

// Stable empty fallback: `query.data ?? []` would mint a new array every render
// while the query is unresolved/errored, which (via DataTable's
// onFilteredRowsChange -> setVisibleRows) would drive an infinite render loop.
const NO_BRANDS: BrandOut[] = [];
import {
  type CandidateDraft,
  CandidateEditor,
  allDraftsVerified,
  draftVerified,
  realDrafts,
} from "../components/CandidateEditor";
import { IconExternal, IconPlus, IconStores } from "../components/icons";
import { useToast } from "../components/toast";
import { Button, Grid, Input, PageHeader, Panel, Row, Switch } from "@akaike/ui";
import { Field, Kpi, Seg, Stat } from "../components/foundation";

export function Brands() {
  const canWrite = useCanWrite();
  const qc = useQueryClient();
  const notify = useToast();
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");
  // Filter to brands with an "unmatched candidate" warning: an enabled search
  // candidate on some platform that has produced 0 active SKUs (see the amber pill).
  const [warnOnly, setWarnOnly] = useState(false);
  const [unsureOnly, setUnsureOnly] = useState(false);
  // Whether combo/bundle SKUs count toward the per-brand + headline SKU numbers. Off by
  // default: the counts then reflect distinct base products, not the multipacks that alias
  // them. Drives the shared sku-counts query (server-side exclusion).
  const [includeCombos, setIncludeCombos] = useState(false);
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // The table's current filtered+sorted rows; the header checkbox acts on these only, so
  // narrowing the filter and clearing there leaves the rest of the selection intact.
  const [visibleRows, setVisibleRows] = useState<BrandOut[]>([]);
  const {
    selected,
    selectedIds,
    allSelected,
    someSelected,
    hiddenCount,
    toggleAll,
    toggleOne,
    clear: clearSelection,
  } = useRowSelection(visibleRows, (b) => b.id);
  // brands queued for a confirmed removal (single row or the bulk selection)
  const [removing, setRemoving] = useState<{
    ids: string[];
    label: string;
  } | null>(null);

  const brandsQuery = useListBrandsV1BrandsGet({
    query: { refetchInterval: 15000 },
  });

  // per-platform active SKU counts ({platform: {brand: count}}): merged by brand
  // name into the platform pills.
  const countsQuery = useQuery({
    queryKey: ["sku-counts", includeCombos],
    queryFn: () => fetchSkuCounts(includeCombos),
    refetchInterval: 30000,
    // Keep the prior figures visible while a combo-toggle flip refetches, so the KPI
    // doesn't blink to 0 and AnimatedNumber doesn't tween down-then-up through zero.
    placeholderData: keepPreviousData,
  });
  const countFor = (platform: Platform, brand: string) =>
    countOf(countsQuery.data, platform, brand);
  // Total active SKUs for a brand across every platform (the sortable computed column).
  // Sorts on the footprint, not the enabled subset: this column answers "how big is this
  // brand", and the enabled figure is already visible inside every pill.
  const totalSkusFor = (b: BrandOut) =>
    platformKeys().reduce((sum, p) => sum + countFor(p, b.brand).total, 0);
  // A platform is "unmatched" for a brand when it has an enabled candidate but 0 SKUs.
  // Mirrors the amber pill's `warnEmpty`; only meaningful once counts have loaded.
  const brandHasWarning = (b: BrandOut) =>
    !countsQuery.isLoading &&
    platformKeys().some(
      (p) =>
        countFor(p, b.brand).total === 0 &&
        (b.candidates ?? []).some((c) => c.platform === p && c.enabled),
    );

  const all = brandsQuery.data ?? NO_BRANDS;
  const invalidate = () => qc.invalidateQueries({ queryKey: getListBrandsV1BrandsGetQueryKey() });

  const toggle = useUpdateBrandV1BrandsBrandIdPatch({
    mutation: {
      onSuccess: (data) => {
        notify(`${data.brand} ${data.enabled ? "activated" : "deactivated"}`);
        invalidate();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });

  const setEnabled = useSetEnabledV1BrandsSetEnabledPost({
    mutation: {
      onSuccess: (rows, vars) => {
        notify(`${rows.length} brand${rows.length === 1 ? "" : "s"} ${vars.data.enabled ? "activated" : "deactivated"}`);
        clearSelection();
        invalidate();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });

  const remove = useBulkDeleteV1BrandsBulkDeletePost({
    mutation: {
      onSuccess: (res) => {
        notify(`${res.deleted} brand${res.deleted === 1 ? "" : "s"} removed`);
        clearSelection();
        setRemoving(null);
        invalidate();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "delete failed", "crit"),
    },
  });

  // Inline per-row priority edit (reuses the generic brand PATCH).
  const setRowPriority = useUpdateBrandV1BrandsBrandIdPatch({
    mutation: {
      onSuccess: (data) => {
        notify(`${data.brand} priority ${data.priority}`);
        invalidate();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });

  const setPriority = useSetPriorityV1BrandsSetPriorityPost({
    mutation: {
      onSuccess: (rows, vars) => {
        notify(`${rows.length} brand${rows.length === 1 ? "" : "s"} set to priority ${vars.data.priority}`);
        clearSelection();
        invalidate();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "update failed", "crit"),
    },
  });
  // The bulk "set priority" input value (applies to the current selection).
  const [bulkPriority, setBulkPriority] = useState("0");

  const busy =
    toggle.isPending ||
    remove.isPending ||
    setEnabled.isPending ||
    setRowPriority.isPending ||
    setPriority.isPending;

  const enabledCount = all.filter((b) => b.enabled).length;
  // Per-platform SKU counts, split THREE ways because two independent switches gate a
  // scrape and collapsing them into one number is actively misleading -- this page used to
  // show `onActiveBrands` while calling it "what a sweep would actually refresh", which
  // over-reported by more than 10x once per-SKU toggles were in use.
  //
  //   tracked         -- every SKU of every tracked brand. The dimension footprint.
  //   onActiveBrands  -- narrowed by brands.enabled only.
  //   scraping        -- ALSO narrowed by sku_overrides.enabled. The real scrape universe.
  //
  // All three are restricted to tracked brand names: the sku-counts endpoint keys off the
  // ClickHouse dimension, which keeps rows for brands that have since been deleted here,
  // and those are not part of "all tracked brands".
  const skuTotals = useMemo(() => {
    const tracked = new Set(all.map((b) => b.brand));
    const enabled = new Set(all.filter((b) => b.enabled).map((b) => b.brand));
    const m = new Map<string, { onActiveBrands: number; scraping: number; tracked: number }>();
    for (const p of platformKeys()) {
      const byBrand = countsQuery.data?.[p] ?? {};
      let onActiveBrands = 0;
      let scraping = 0;
      let total = 0;
      for (const [brand, c] of Object.entries(byBrand)) {
        if (!tracked.has(brand)) continue;
        total += c.total;
        if (enabled.has(brand)) {
          onActiveBrands += c.total;
          scraping += c.enabled;
        }
      }
      m.set(p, { onActiveBrands, scraping, tracked: total });
    }
    return m;
  }, [countsQuery.data, all]);
  type SkuTotalKey = "onActiveBrands" | "scraping" | "tracked";
  const sumOver = (key: SkuTotalKey) =>
    platformKeys().reduce((a, p) => a + (skuTotals.get(p)?.[key] ?? 0), 0);
  // "Blinkit 328, Zepto 269, Instamart 230" for whichever figure is being broken down.
  // Shared so a breakdown can never drift from the total it sits under.
  const platformBreakdown = (key: SkuTotalKey) =>
    platformKeys()
      .map((p) => `${platformLabel(p)} ${(skuTotals.get(p)?.[key] ?? 0).toLocaleString()}`)
      .join(" · ");
  const activeSkuCount = sumOver("onActiveBrands");
  const scrapingSkuCount = sumOver("scraping");
  const totalSkuCount = sumOver("tracked");

  // Groups whose weakest member scored below the review bar, per brand. One query for the
  // whole table: asking per row would be 130 round trips to render one chip.
  const unsureQuery = useLowConfidenceCountsV1BrandsMatchesLowConfidenceGet();
  const unsureFor = (b: BrandOut) => unsureQuery.data?.[b.brand] ?? 0;
  const unsureBrands = useMemo(
    () => all.filter((b) => (unsureQuery.data?.[b.brand] ?? 0) > 0).length,
    [all, unsureQuery.data],
  );

  // How many tracked brands currently carry the unmatched-candidate warning.
  const warnedCount = useMemo(
    () => all.filter(brandHasWarning).length,
    // brandHasWarning closes over countsQuery; re-count when brands or counts change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, countsQuery.data, countsQuery.isLoading],
  );

  // Coarse active/inactive (+ optional unmatched-only) pre-filter feeds the table;
  // the table owns search + sort + column visibility on top of it.
  const data = useMemo(
    () =>
      all.filter(
        (b) =>
          (activeFilter === "all" ? true : activeFilter === "active" ? b.enabled : !b.enabled) &&
          (!warnOnly || brandHasWarning(b)) &&
          (!unsureOnly || unsureFor(b) > 0),
      ),
    // brandHasWarning closes over countsQuery; re-filter when counts change too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, activeFilter, warnOnly, unsureOnly, unsureQuery.data, countsQuery.data, countsQuery.isLoading],
  );

  const columns = useMemo<AnyColumnDef<BrandOut>[]>(() => {
    const col = createColumnHelper<BrandOut>();
    return [
      col.display({
        id: "select",
        // Selection stays on for viewers: it also scopes the read-only SKU download.
        header: () => (
          <SelectAllCheckbox
            checked={allSelected}
            indeterminate={someSelected}
            hiddenCount={hiddenCount}
            onChange={toggleAll}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label={`select ${row.original.brand}`}
            checked={selected.has(row.original.id)}
            onChange={() => toggleOne(row.original.id)}
          />
        ),
        meta: { align: "center", width: 40 },
        enableSorting: false,
        enableHiding: false,
      }),
      col.accessor("brand", {
        header: "Brand",
        cell: ({ row }) => <Link to={`/brands/${row.original.id}`}>{row.original.brand}</Link>,
        meta: { cellClassName: "strong", minWidth: 180 },
      }),
      col.display({
        id: "platforms",
        header: "Platforms · SKUs",
        cell: ({ row }) => (
          <div className="row" style={{ gap: 8, flexWrap: "nowrap" }}>
            {platformKeys().map((p) => {
              const c = countFor(p, row.original.brand);
              // A configured, enabled search candidate that has produced 0 active SKUs:
              // the storefront's brand label likely differs from the search term (needs an
              // alias) or the sweep hasn't run yet. Only warn once counts have loaded, and
              // only when a candidate actually exists (a platform with no candidate is a
              // legitimate 0, not a problem).
              const hasEnabledCandidate = (row.original.candidates ?? []).some(
                (c2) => c2.platform === p && c2.enabled,
              );
              const warnEmpty = !countsQuery.isLoading && c.total === 0 && hasEnabledCandidate;
              // Show "enabled/total" only when they differ. Rendering "142/142" on every
              // untouched brand would be noise, and a bare number reading as the scrape
              // count is exactly the confusion this split exists to remove.
              const split = c.enabled !== c.total;
              return (
                <Link
                  key={p}
                  to={`/brands/${row.original.id}?tab=${p}`}
                  className={`plat ${p}${warnEmpty ? " warn-empty" : ""}`}
                  title={
                    warnEmpty
                      ? `${platformLabel(p)}: a search candidate is configured but 0 SKUs matched. The storefront's brand label may differ from the search term; open to whitelist an alias, or run a sweep.`
                      : split
                        ? `${platformLabel(p)}, ${c.enabled} of ${c.total} active SKUs enabled for scraping (${c.total - c.enabled} switched off per-SKU) · view SKUs`
                        : `${platformLabel(p)}, ${c.total} active SKUs, all enabled · view SKUs`
                  }
                  style={{ opacity: c.total > 0 || warnEmpty ? 1 : 0.45 }}
                >
                  <span className="num" style={{ fontSize: 11.5 }}>
                    {countsQuery.isLoading
                      ? "…"
                      : split
                        ? `${c.enabled.toLocaleString()}/${c.total.toLocaleString()}`
                        : c.total.toLocaleString()}
                  </span>
                  {/* No own `title` on the glyph: hovering it then falls through to the
                      Link's explanatory tooltip (an empty title here would suppress it). */}
                  {warnEmpty && (
                    <span aria-hidden style={{ fontSize: 11 }}>
                      ⚠
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ),
        meta: { minWidth: 200 },
        enableSorting: false,
      }),
      // Computed total SKUs across platforms. A function accessor (not a BrandOut
      // field) so the table can sort numerically on the summed count; the value comes
      // from the separate counts query, so it re-derives when those load/refresh.
      col.accessor((b) => totalSkusFor(b), {
        id: "total_skus",
        header: "Total SKUs",
        cell: ({ getValue }) => (
          <span className="num" style={{ fontSize: 12 }}>
            {countsQuery.isLoading ? "…" : getValue().toLocaleString()}
          </span>
        ),
        meta: {
          align: "right",
          width: 110,
          exportValue: (b) => String(totalSkusFor(b)),
        },
      }),
      // Groups the model hedged on. A LINK, not a number: the only useful thing to do with
      // a review count is go and review it, and the destination arrives pre-filtered.
      col.accessor((b) => unsureFor(b), {
        id: "low_confidence",
        header: "Low conf.",
        cell: ({ row, getValue }) =>
          getValue() > 0 ? (
            <Link
              to={`/brands/${row.original.id}?tab=matched&review=low`}
              className="num"
              style={{ fontSize: 12, color: "var(--warn)", fontWeight: 600 }}
              title="review the matched SKUs this brand is least sure about"
            >
              {getValue()}
            </Link>
          ) : (
            <span className="num faint" style={{ fontSize: 12 }}>
              {unsureQuery.isLoading ? "…" : "0"}
            </span>
          ),
        meta: {
          align: "right",
          width: 96,
          exportValue: (b) => String(unsureFor(b)),
        },
      }),
      col.accessor("enabled", {
        header: "Active",
        cell: ({ row }) => canWrite ? (
          <Switch
            checked={row.original.enabled}
            disabled={busy}
            onCheckedChange={() =>
              toggle.mutate({
                brandId: row.original.id,
                data: { enabled: !row.original.enabled },
              })
            }
            aria-label="toggle active"
          />
        ) : (
          <span>{row.original.enabled ? "Yes" : "No"}</span>
        ),
        meta: {
          align: "center",
          width: 90,
          exportValue: (b) => (b.enabled ? "yes" : "no"),
        },
      }),
      col.accessor("priority", {
        header: "Priority",
        cell: ({ row }) => (
          <PriorityStepper
            value={row.original.priority}
            disabled={busy}
            onCommit={(priority) => {
              if (priority !== row.original.priority)
                setRowPriority.mutate({
                  brandId: row.original.id,
                  data: { priority },
                });
            }}
          />
        ),
        meta: {
          align: "center",
          width: 132,
          exportValue: (b) => String(b.priority),
        },
      }),
      col.accessor("added_by", {
        header: "Added by",
        cell: ({ row }) =>
          row.original.added_by ? (
            <span className="mono" style={{ fontSize: 11, color: "var(--ink-2)" }}>
              {row.original.added_by}
            </span>
          ) : (
            <span className="faint">—</span>
          ),
        meta: { minWidth: 140 },
      }),
      col.accessor("created_at", {
        header: "Added",
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 11.5 }}>
            {ago(row.original.created_at)}
          </span>
        ),
        meta: { width: 120, exportValue: (b) => b.created_at },
      }),
      col.display({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => canWrite ? (
          <Button
            variant="destructive"
            size="sm"
            disabled={busy}
            onClick={() =>
              setRemoving({
                ids: [row.original.id],
                label: `"${row.original.brand}"`,
              })
            }
          >
            Remove
          </Button>
        ) : null,
        meta: { align: "right", width: 110 },
        enableSorting: false,
        enableHiding: false,
      }),
    ];
    // countFor/countsQuery/toggle are stable enough; re-memo on the values that
    // affect rendered cells.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allSelected,
    someSelected,
    hiddenCount,
    selected,
    busy,
    canWrite,
    countsQuery.data,
    countsQuery.isLoading,
    // the Low conf. cell reads this query, so the column has to re-memo when it lands
    unsureQuery.data,
    unsureQuery.isLoading,
  ]);

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Brands"
        description="Tracked brands → brand sweep (all platforms) → SKUs"
        action={
          <Row gap="sm" className="flex-wrap justify-end">
            <Button asChild variant="outline">
              <Link to="/brands/probe-locations" title="Configure the city points the brand-search test walks">
                <IconStores />
                Probe locations
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => setDownloading(true)}
              title="Download SKUs across brands as CSV, filtered by platform, combos, and either enable switch"
            >
              <Download size={14} />
              Download SKUs
            </Button>
            {canWrite && (
              <>
                <Button variant="outline" onClick={() => setBulk(true)}>
                  Bulk import
                </Button>
                <Button variant="primary" onClick={() => setAdding(true)}>
                  <IconPlus />
                  New brand
                </Button>
              </>
            )}
          </Row>
        }
      />

      <Grid cols={4} gap="md" className="mb-3.5">
        <Kpi label="tracked brands" value={<AnimatedNumber value={all.length} />} foot="swept on every platform" />
        <Kpi label="active" value={<AnimatedNumber value={enabledCount} />} tone="ok" foot={`${all.length - enabledCount} inactive`} />
        <Stat
          surface
          label={`SKUs on active brands${includeCombos ? "" : " (excl. combos)"}`}
          value={<AnimatedNumber value={activeSkuCount} />}
          foot={
            // Each figure owns the per-platform breakdown directly beneath it (tight gap
            // inside a pair, looser gap between pairs), so it is never ambiguous which
            // number a breakdown belongs to.
            <div className="col" style={{ gap: 7 }}>
              <div className="col" style={{ gap: 1 }}>
                <span className="faint">{platformBreakdown("onActiveBrands")}</span>
              </div>
              {/* The headline counts brands.enabled only. This pair also nets out the
                  per-SKU master toggle, so it is what a run would actually probe -- called
                  out separately because the two used to be conflated and read 10x high. */}
              <div
                className="col"
                style={{ gap: 1 }}
                title="Also nets out the per-SKU enable toggle (sku_overrides.enabled), so this is what a cart-inventory or carriage run would actually probe today."
              >
                <span style={{ color: "var(--ok)" }}>
                  {scrapingSkuCount.toLocaleString()} will actually scrape
                  {activeSkuCount > scrapingSkuCount && (
                    <span className="faint">
                      {" "}
                      · {(activeSkuCount - scrapingSkuCount).toLocaleString()} switched off per-SKU
                    </span>
                  )}
                </span>
                {/* Tinted like its own number rather than `faint`, so it reads as part of
                    the scrape pair and not as a second breakdown of the headline. */}
                <span style={{ color: "var(--ok)", opacity: 0.72 }}>
                  {platformBreakdown("scraping")}
                </span>
              </div>
              <span
                className="faint"
                title="Every active SKU across all tracked brands, deactivated brands included. Ignores both toggles."
              >
                {totalSkuCount.toLocaleString()} across all {all.length} tracked brands
              </span>
              <span
                className="row"
                style={{ gap: 6, alignItems: "center", fontSize: 11 }}
                title="Combo/bundle SKUs are multipacks that alias a base product. Off = count distinct base products only. Applies to all three numbers above."
              >
                <Switch
                  checked={includeCombos}
                  onCheckedChange={setIncludeCombos}
                  aria-label="include combo/bundle SKUs in the counts"
                />
                <span className="faint">include combos</span>
              </span>
            </div>
          }
        />
        <Kpi
          label="platforms"
          value={<AnimatedNumber value={platformKeys().length} />}
          foot={platformKeys().map(platformLabel).join(" · ")}
        />
      </Grid>

      {brandsQuery.isError && (
        <Panel className="mb-3.5">
          <Panel.Body className="text-crit">
            Could not load brands. Is the control plane up?
          </Panel.Body>
        </Panel>
      )}

      <Panel>
        <Panel.Head>
          {selected.size > 0 ? (
            <>
              <SelectionCount count={selected.size} hiddenCount={hiddenCount} />
              {canWrite && (
              <>
              <Button
                variant="success"
                size="sm"
                style={{ marginLeft: 10 }}
                disabled={busy}
                onClick={() =>
                  setEnabled.mutate({
                    data: { enabled: true, ids: selectedIds },
                  })
                }
              >
                Activate
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={() =>
                  setEnabled.mutate({
                    data: { enabled: false, ids: selectedIds },
                  })
                }
              >
                Deactivate
              </Button>
              <span className="faint" style={{ marginLeft: 10, fontSize: 12 }}>
                Set priority
              </span>
              <Input
                type="number"
                min={0}
                value={bulkPriority}
                onChange={(e) => setBulkPriority(e.currentTarget.value)}
                style={{ width: 60 }}
                aria-label="bulk priority value"
              />
              <Button
                size="sm"
                disabled={busy}
                onClick={() =>
                  setPriority.mutate({
                    data: {
                      priority: Math.max(0, Math.floor(Number(bulkPriority) || 0)),
                      ids: selectedIds,
                    },
                  })
                }
              >
                Apply
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={() =>
                  setRemoving({
                    ids: selectedIds,
                    label: `${selected.size} ${selected.size === 1 ? "brand" : "brands"}`,
                  })
                }
              >
                Remove
              </Button>
              </>
              )}
              <Button variant="outline" size="sm" disabled={busy} onClick={clearSelection}>
                Clear
              </Button>
            </>
          ) : (
            <span className="text-[12.5px] font-semibold whitespace-nowrap text-foreground">Tracked brands</span>
          )}
          <div className="flex-1" />
          {canWrite && <Button
            variant="success"
            size="sm"
            disabled={busy || all.length === 0}
            onClick={() => setEnabled.mutate({ data: { enabled: true, ids: null } })}
          >
            Activate all
          </Button>}
          {canWrite && <Button
            variant="destructive"
            size="sm"
            disabled={busy || all.length === 0}
            onClick={() => setEnabled.mutate({ data: { enabled: false, ids: null } })}
          >
            Deactivate all
          </Button>}
        </Panel.Head>
        <Panel.Body tight>
          <DataTable
            tableKey="brands"
            fillHeight
            columns={columns}
            data={data}
            getRowId={(b) => b.id}
            globalPlaceholder="Search brand…"
            csvFilename="brands"
            rowClassName={(b) => (b.enabled ? undefined : "row-dim")}
            onFilteredRowsChange={setVisibleRows}
            emptyMessage="No brands match."
            isLoading={brandsQuery.isLoading}
            toolbarExtras={
              <Row gap="sm">
                <Seg
                  brand
                  value={activeFilter}
                  options={[
                    { value: "all", label: "all" },
                    { value: "active", label: "active" },
                    { value: "inactive", label: "inactive" },
                  ]}
                  onChange={setActiveFilter}
                />
                <Button
                  size="sm"
                  aria-pressed={warnOnly}
                  disabled={warnedCount === 0 && !warnOnly}
                  title="Show only brands with a search candidate configured but 0 SKUs matched on some platform"
                  onClick={() => setWarnOnly((v) => !v)}
                  style={
                    warnOnly
                      ? {
                          borderColor: "var(--warn)",
                          color: "var(--warn)",
                          fontWeight: 700,
                        }
                      : undefined
                  }
                >
                  ⚠ Unmatched{warnedCount > 0 ? ` (${warnedCount})` : ""}
                </Button>
                <Button
                  size="sm"
                  aria-pressed={unsureOnly}
                  disabled={unsureBrands === 0 && !unsureOnly}
                  title="Show only brands with matched SKUs whose weakest member scored below the review bar"
                  onClick={() => setUnsureOnly((v) => !v)}
                  style={
                    unsureOnly
                      ? {
                          borderColor: "var(--warn)",
                          color: "var(--warn)",
                          fontWeight: 700,
                        }
                      : undefined
                  }
                >
                  Low confidence{unsureBrands > 0 ? ` (${unsureBrands})` : ""}
                </Button>
              </Row>
            }
          />
        </Panel.Body>
      </Panel>

      {canWrite && adding && (
        <AddBrand
          onClose={() => setAdding(false)}
          onSaved={() => {
            invalidate();
            setAdding(false);
          }}
        />
      )}

      {canWrite && bulk && (
        <BulkImport
          onClose={() => setBulk(false)}
          onCommitted={() => {
            invalidate();
            qc.invalidateQueries({ queryKey: ["sku-counts"] });
            setBulk(false);
          }}
        />
      )}

      {downloading && (
        <SkuDownloadModal
          onClose={() => setDownloading(false)}
          // Only the ticked rows that survive the current page filter, so the "only these"
          // scope matches what is actually on screen.
          selectedBrands={visibleRows.filter((b) => selected.has(b.id)).map((b) => b.brand)}
          defaults={{
            brandState: activeFilter === "all" ? "all" : activeFilter === "active" ? "enabled" : "disabled",
            combos: includeCombos ? "all" : "exclude",
          }}
        />
      )}

      {canWrite && removing && (
        <ConfirmDialog
          title="Remove brands"
          tone="danger"
          confirmLabel={`Remove ${removing.ids.length === 1 ? "brand" : `${removing.ids.length} brands`}`}
          busy={remove.isPending}
          onClose={() => setRemoving(null)}
          onConfirm={() => remove.mutate({ data: { ids: removing.ids } })}
        >
          Remove {removing.label}? This deletes the tracked {removing.ids.length === 1 ? "brand" : "brands"} and
          stops {removing.ids.length === 1 ? "it" : "them"} being swept. Already-collected SKUs in ClickHouse are
          unaffected.
        </ConfirmDialog>
      )}
    </>
  );
}

// Styled confirmation modal (replaces window.confirm). Click-away or Cancel
// dismisses; the primary action is tinted by `tone`.
function ConfirmDialog({
  title,
  children,
  confirmLabel,
  tone,
  busy,
  onConfirm,
  onClose,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  tone?: "danger";
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <Panel style={{ width: "100%", maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <Panel.Head title={title}>
          <div className="flex-1" />
          <Button variant="outline" size="sm" icon onClick={onClose} aria-label="close">
            ✕
          </Button>
        </Panel.Head>
        <Panel.Body style={{ color: "var(--ink-2)", fontSize: 12.5, lineHeight: 1.5 }}>{children}</Panel.Body>
        <Panel.Head
          style={{
            borderTop: "1px solid var(--line)",
            borderBottom: 0,
            justifyContent: "flex-end",
          }}
          >
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button variant={tone === "danger" ? "destructive" : "primary"} disabled={busy} onClick={onConfirm} autoFocus>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </Panel.Head>
      </Panel>
    </div>
  );
}

function AddBrand({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const notify = useToast();
  const [name, setName] = useState("");
  const [drafts, setDrafts] = useState<CandidateDraft[]>([]);

  const candidates = realDrafts(drafts);
  const verifiedCount = candidates.filter(draftVerified).length;
  const allVerified = allDraftsVerified(drafts);

  const create = useCreateBrandV1BrandsPost({
    mutation: {
      onSuccess: () => {
        notify(`${name.trim()} registered`, "ok");
        onSaved();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "register failed", "crit"),
    },
  });
  const submit = () =>
    create.mutate({
      data: {
        brand: name.trim(),
        enabled: true,
        candidates: candidates.map((d) => ({
          platform: d.platform,
          search_term: d.search_term.trim(),
          match_aliases: d.match_aliases,
          enabled: true,
          // verification proof from the just-run probe (the server requires it)
          verified: draftVerified(d),
          last_test_hit_count: d.result?.hit_count ?? null,
          last_test_sample: d.result?.products ?? null,
        })),
      },
    });

  // Hard gate: a brand can only be registered once every search candidate is verified.
  const canSave = name.trim().length > 0 && allVerified && !create.isPending;
  const gateHint =
    candidates.length === 0
      ? "add and verify at least one search candidate"
      : !allVerified
        ? `verify all candidates to register (${verifiedCount}/${candidates.length} done)`
        : "";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "48px 16px",
      }}
      onClick={onClose}
    >
      <Panel style={{ width: "100%", maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <Panel.Head title="Register a brand">
          <div className="flex-1" />
          <Button variant="outline" size="sm" icon onClick={onClose} aria-label="close">
            ✕
          </Button>
        </Panel.Head>
        <Panel.Body
          className="flex flex-col gap-3.5 overflow-auto"
          style={{ maxHeight: "70vh" }}
        >
          <Field
            label="Canonical brand name"
            hint={
              <>
                the single reporting label · stored as the ClickHouse{" "}
                <code className="mono">brand</code>
              </>
            }
          >
            <Input
              autoFocus
              placeholder="e.g. The Moms Co"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
          </Field>

          <Field label="Per-platform search candidates">
            <CandidateEditor drafts={drafts} onChange={setDrafts} defaultTerm={name.trim()} />
            <span className="hint" style={{ marginTop: 6 }}>
              add a candidate per platform and <strong>Test</strong> each one - a brand can only be
              registered once every candidate is verified against the live platform search.
            </span>
            <span className="hint" style={{ marginTop: 6 }}>
              Test not finding your brand? Check the cities it searches -{" "}
              <Link
                to="/brands/probe-locations"
                target="_blank"
                rel="noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                Probe locations
                <span className="ico-14">
                  <IconExternal />
                </span>
              </Link>
            </span>
          </Field>
        </Panel.Body>
        <Panel.Head
          style={{
            borderTop: "1px solid var(--line)",
            borderBottom: 0,
            justifyContent: "flex-end",
          }}
        >
          {candidates.length > 0 && (
            <span
              className="num"
              style={{
                fontSize: 11.5,
                marginRight: "auto",
                color: allVerified ? "var(--ok)" : "var(--warn)",
              }}
            >
              {allVerified ? "✓ " : ""}
              {verifiedCount}/{candidates.length} verified
            </span>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSave} title={gateHint} onClick={submit}>
            {create.isPending ? "Saving…" : "Register brand"}
          </Button>
        </Panel.Head>
      </Panel>
    </div>
  );
}
